/**
 * 票链云 — 发票查验要素规则
 *
 * 【为什么需要这个文件】
 * 发票查验失败的头号原因不是接口问题，而是**要素给错**——不同票种要求的
 * 查验要素完全不同：专票要「不含税金额」，普票要「校验码后 6 位」，
 * 二手车要「车价合计」，数电票则连发票代码都没有。
 * 这些规则同时被三处使用，因此必须集中定义：
 *   1. 前端表单动态提示「本票种需要填写什么」
 *   2. 后端调用验真接口前的本地预校验（避免无效调用产生费用）
 *   3. 人工录入界面做格式校验
 *
 * 【重要】本地预校验能省钱：阿里云验真通道对「信息不一致(006)」「查无此票(009)」
 * 「四要素不规范(1005)」同样计费。先本地拦掉格式错误，能避免无谓扣费。
 */
import { InvoiceTypeCode, E_INVOICE_TYPE_CODES } from './enums.js';
/** 验真时「金额」字段应填什么口径 */
export const VerifyAmountKind = {
    /** 不含税金额 */
    NET: 'NET',
    /** 含税金额（价税合计） */
    GROSS: 'GROSS',
    /** 车价合计 */
    CAR_PRICE: 'CAR_PRICE',
    /** 不需要金额 */
    NONE: 'NONE',
};
export const VERIFY_AMOUNT_KIND_LABELS = {
    NET: '不含税金额',
    GROSS: '含税金额（价税合计）',
    CAR_PRICE: '车价合计',
    NONE: '无需填写',
};
/** 传统发票（有代码、有校验码）的通用形态 */
const TRADITIONAL = {
    needInvoiceCode: true,
    invoiceNoLength: 8,
    invoiceCodeLength: 12,
};
/** 数电票（全电发票）：无发票代码，号码 20 位 */
const E_INVOICE = {
    needInvoiceCode: false,
    amountKind: VerifyAmountKind.GROSS,
    needVerifyCode: false,
    invoiceNoLength: 20,
    invoiceCodeLength: null,
};
/**
 * 票种 → 查验要素对照表。
 *
 * 依据阿里云 VerifyVATInvoice 的发票类型代码表整理，
 * 该口径与税务系统一致，乐企等其它通道通用。
 */
export const VERIFY_REQUIREMENTS = {
    // —— 专票类：填不含税金额 ——
    [InvoiceTypeCode.VAT_SPECIAL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NET,
        needVerifyCode: false,
    },
    [InvoiceTypeCode.VAT_E_SPECIAL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NET,
        needVerifyCode: false,
    },
    // —— 货运专票：填合计金额 ——
    [InvoiceTypeCode.FREIGHT_SPECIAL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NET,
        needVerifyCode: false,
    },
    // —— 机动车销售统一发票：填不含税价 ——
    [InvoiceTypeCode.VEHICLE_SALE]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NET,
        needVerifyCode: false,
    },
    // —— 普票类：填校验码后 6 位 ——
    [InvoiceTypeCode.VAT_GENERAL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: true,
        verifyCodeHint: '填写校验码后 6 位',
    },
    [InvoiceTypeCode.VAT_ELECTRONIC]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: true,
        verifyCodeHint: '填写校验码后 6 位',
    },
    [InvoiceTypeCode.VAT_ROLL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: true,
        verifyCodeHint: '填写校验码后 6 位',
    },
    [InvoiceTypeCode.VAT_TOLL]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: true,
        verifyCodeHint: '填写校验码后 6 位',
    },
    // —— 二手车：填车价合计 ——
    [InvoiceTypeCode.USED_VEHICLE]: {
        ...TRADITIONAL,
        amountKind: VerifyAmountKind.CAR_PRICE,
        needVerifyCode: false,
    },
    // —— 数电票系列：无代码、无校验码，填含税金额 ——
    [InvoiceTypeCode.E_INVOICE_SPECIAL]: E_INVOICE,
    [InvoiceTypeCode.E_INVOICE_GENERAL]: E_INVOICE,
    [InvoiceTypeCode.E_TRAIN_TICKET]: E_INVOICE,
    [InvoiceTypeCode.E_AIR_TICKET]: E_INVOICE,
    [InvoiceTypeCode.E_VEHICLE_SALE]: E_INVOICE,
    [InvoiceTypeCode.E_USED_VEHICLE]: E_INVOICE,
    // —— 数电纸质专票：有代码，填不含税金额 ——
    [InvoiceTypeCode.E_PAPER_SPECIAL]: {
        needInvoiceCode: true,
        amountKind: VerifyAmountKind.NET,
        needVerifyCode: false,
        invoiceNoLength: 20,
        invoiceCodeLength: null,
    },
    // —— 数电纸质普票：校验码填「密码区数电票号码后六位」——
    [InvoiceTypeCode.E_PAPER_GENERAL]: {
        needInvoiceCode: true,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: true,
        verifyCodeHint: '填写密码区数电票号码的后 6 位（注意不是校验码后 6 位）',
        invoiceNoLength: 20,
        invoiceCodeLength: null,
    },
    [InvoiceTypeCode.E_PAPER_VEHICLE]: {
        needInvoiceCode: true,
        amountKind: VerifyAmountKind.CAR_PRICE,
        needVerifyCode: false,
        invoiceNoLength: 20,
        invoiceCodeLength: null,
    },
    [InvoiceTypeCode.E_PAPER_USED_VEHICLE]: {
        needInvoiceCode: true,
        amountKind: VerifyAmountKind.CAR_PRICE,
        needVerifyCode: false,
        invoiceNoLength: 20,
        invoiceCodeLength: null,
    },
    // —— 未知票种：按最宽松处理，全部要素由人工判断 ——
    [InvoiceTypeCode.UNKNOWN]: {
        needInvoiceCode: false,
        amountKind: VerifyAmountKind.NONE,
        needVerifyCode: false,
        invoiceNoLength: null,
        invoiceCodeLength: null,
    },
};
/** 是否为数电票（全电发票） */
export function isEInvoice(type) {
    return E_INVOICE_TYPE_CODES.includes(type);
}
/** 统一清洗：去空格、全角转半角、去掉常见分隔符 */
export function normalizeDigits(input) {
    if (!input)
        return '';
    return input
        .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
        .replace(/[\s\-_]/g, '')
        .trim();
}
/** 金额规范化：去掉 ¥ 符号、千分位逗号，保留两位小数 */
export function normalizeAmount(input) {
    if (input === null || input === undefined || input === '')
        return '';
    const raw = String(input).replace(/[¥￥,\s]/g, '');
    const num = Number(raw);
    if (!Number.isFinite(num))
        return '';
    return num.toFixed(2);
}
/** 日期规范化：支持 2026年09月07日 / 2026-09-07 / 20260907 三种写法 */
export function normalizeInvoiceDate(input) {
    if (!input)
        return '';
    const s = String(input).trim();
    // 2026年09月07日
    const cn = s.match(/^(\d{4})\D+(\d{1,2})\D+(\d{1,2})\D*$/);
    if (cn) {
        return `${cn[1]}${cn[2].padStart(2, '0')}${cn[3].padStart(2, '0')}`;
    }
    // 2026-09-07
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
        return `${iso[1]}${iso[2].padStart(2, '0')}${iso[3].padStart(2, '0')}`;
    }
    // 20260907
    const compact = normalizeDigits(s);
    if (/^\d{8}$/.test(compact))
        return compact;
    return '';
}
/**
 * 调用验真接口前的本地预校验。
 *
 * 目的有二：
 *   1. 给用户明确的「该填什么」提示，而不是等接口返回模糊错误
 *   2. 避免因格式错误发起无效调用（验真通道对部分失败结果同样计费）
 */
export function precheckVerifyInput(input) {
    const errors = [];
    const req = VERIFY_REQUIREMENTS[input.invoiceType] ?? VERIFY_REQUIREMENTS[InvoiceTypeCode.UNKNOWN];
    const invoiceNo = normalizeDigits(input.invoiceNo);
    const invoiceCode = normalizeDigits(input.invoiceCode);
    const invoiceDate = normalizeInvoiceDate(input.invoiceDate);
    const amount = normalizeAmount(input.amount);
    const verifyCode = normalizeDigits(input.verifyCode);
    if (!invoiceNo) {
        errors.push('发票号码未填写');
    }
    else if (req.invoiceNoLength !== null && invoiceNo.length !== req.invoiceNoLength) {
        errors.push(`发票号码应为 ${req.invoiceNoLength} 位，当前为 ${invoiceNo.length} 位（${invoiceNo}）`);
    }
    if (req.needInvoiceCode) {
        if (!invoiceCode) {
            errors.push('该票种需要发票代码，但未填写');
        }
        else if (req.invoiceCodeLength !== null &&
            invoiceCode.length !== req.invoiceCodeLength) {
            errors.push(`发票代码应为 ${req.invoiceCodeLength} 位，当前为 ${invoiceCode.length} 位`);
        }
    }
    if (!invoiceDate) {
        errors.push('开票日期未填写或格式无法识别（支持 2026年09月07日 / 2026-09-07 / 20260907）');
    }
    if (req.amountKind !== VerifyAmountKind.NONE) {
        if (!amount) {
            errors.push(`该票种查验需要填写${VERIFY_AMOUNT_KIND_LABELS[req.amountKind]}`);
        }
        else if (Number(amount) <= 0) {
            errors.push(`${VERIFY_AMOUNT_KIND_LABELS[req.amountKind]}应大于 0`);
        }
    }
    if (req.needVerifyCode) {
        if (!verifyCode) {
            errors.push(`${req.verifyCodeHint ?? '需要填写校验码后 6 位'}`);
        }
        else if (verifyCode.length !== 6) {
            errors.push(`校验码应取后 6 位，当前为 ${verifyCode.length} 位${req.verifyCodeHint ? `（${req.verifyCodeHint}）` : ''}`);
        }
    }
    const normalized = { invoiceType: input.invoiceType, invoiceNo, invoiceDate };
    if (invoiceCode)
        normalized.invoiceCode = invoiceCode;
    if (amount)
        normalized.amount = amount;
    if (verifyCode)
        normalized.verifyCode = verifyCode;
    return { ok: errors.length === 0, errors, normalized };
}
/**
 * 从完整校验码中取后 6 位。
 * 用户在票面上看到的是 20 位校验码，接口只收后 6 位 —— 这是最常犯的错误。
 */
export function extractVerifyCodeTail(fullCheckCode) {
    const digits = normalizeDigits(fullCheckCode);
    if (!digits)
        return '';
    return digits.slice(-6);
}
//# sourceMappingURL=taxRules.js.map